import * as React from "react";

import { type AddressTier, formatAddressTier, pickAddressTier } from "./addressTiers";

let measureContext: CanvasRenderingContext2D | null | undefined;

/** One shared 2D context for text measurement; `null` where there is no
 *  canvas (jsdom), which leaves every address at `full`. */
function getMeasureContext(): CanvasRenderingContext2D | null {
    if (measureContext !== undefined) return measureContext;
    try {
        measureContext = typeof document === "undefined" ? null : document.createElement("canvas").getContext("2d");
    } catch {
        measureContext = null;
    }
    return measureContext;
}

/** Content-box width: what text inside `box` can actually use. */
function contentWidth(box: HTMLElement): number {
    const style = getComputedStyle(box);
    const padding = Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight);
    return box.clientWidth - (Number.isFinite(padding) ? padding : 0);
}

export interface UseResponsiveAddressOptions {
    /** The full address. Empty string: nothing to fit (returns ""). */
    address: string;
    /** The element that draws the text (its font is what gets measured). */
    ref: React.RefObject<HTMLElement | null>;
    /**
     * Whose width is the budget: the element itself (`self`, a block or an
     * input), or its parent (`parent`, for an inline address in a sentence,
     * whose own width is just its text).
     */
    measure?: "self" | "parent";
    /** Text sharing the box before / after the address (e.g. a placeholder lead). */
    prefix?: string;
    suffix?: string;
    /** `false` pins the address to `full` and skips observing. */
    enabled?: boolean;
}

/**
 * The address text that fits its box, re-picked whenever the box resizes
 * (ResizeObserver on the element's own box, or its parent's; never window
 * breakpoints). Widths are measured once per font with a canvas, and the
 * chosen tier is memoised per box width.
 */
export function useResponsiveAddress({
    address,
    ref,
    measure = "self",
    prefix = "",
    suffix = "",
    enabled = true
}: UseResponsiveAddressOptions): { text: string; tier: AddressTier } {
    const [tier, setTier] = React.useState<AddressTier>("full");

    React.useLayoutEffect(() => {
        const element = ref.current;
        const box = measure === "parent" ? element?.parentElement : element;
        if (!enabled || !element || !box || !address) {
            setTier("full");
            return undefined;
        }

        // Tier per box width, for this address + font. Cleared on any
        // dependency change by the effect re-running.
        const byWidth = new Map<number, AddressTier>();
        let font = "";

        const update = (): void => {
            const context = getMeasureContext();
            if (!context) return;
            const style = getComputedStyle(element);
            const nextFont = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
            if (nextFont !== font) {
                font = nextFont;
                byWidth.clear();
            }
            const width = Math.floor(contentWidth(box));
            let next = byWidth.get(width);
            if (next === undefined) {
                context.font = font;
                if ("letterSpacing" in context) {
                    context.letterSpacing = style.letterSpacing;
                }
                next = pickAddressTier(address, width, (text) => Math.ceil(context.measureText(text).width), {
                    prefix,
                    suffix
                });
                byWidth.set(width, next);
            }
            setTier(next);
        };

        update();
        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
        observer?.observe(box);
        // Web fonts land after first paint; a fallback face measures wrong.
        void document.fonts?.ready.then(update);
        return (): void | undefined => observer?.disconnect();
    }, [address, enabled, measure, prefix, ref, suffix]);

    return { text: formatAddressTier(address, tier), tier };
}
